import React from 'react';
import { utilizationColor, formatQps } from '../../validation/traffic-types';

/**
 * Badge showing node utilization during simulation
 * Designed to be overlaid on canvas nodes
 */
const NodeUtilizationBadge = ({
  utilization = 0,
  qps = 0,
  maxQps = 0,
  isBottleneck = false,
  isFailed = false,
  compact = false
}) => {
  const color = utilizationColor(utilization);
  const percentage = Math.round(utilization * 100);

  if (isFailed) {
    return (
      <div className="absolute -top-2 -right-2 z-10">
        <div className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
          FAILED
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className="absolute -top-2 -right-2 z-10"
        title={`${percentage}% utilization (${formatQps(qps)} / ${formatQps(maxQps)} QPS)`}
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-lg"
          style={{
            backgroundColor: color,
            color: percentage > 70 ? 'white' : 'black'
          }}
        >
          {percentage}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-10">
      <div
        className={`bg-slate-800 rounded-lg shadow-lg border px-2 py-1 text-xs whitespace-nowrap ${
          isBottleneck ? 'border-yellow-500 animate-pulse' : 'border-slate-600'
        }`}
      >
        <div className="flex items-center gap-2">
          {/* Utilization bar */}
          <div className="w-12 h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(percentage, 100)}%`,
                backgroundColor: color
              }}
            />
          </div>

          {/* Percentage */}
          <span style={{ color }} className="font-bold">
            {percentage}%
          </span>
        </div>

        {/* QPS display */}
        <div className="text-slate-400 text-center mt-0.5">
          {formatQps(qps)}
        </div>
      </div>

      {/* Arrow pointing to node */}
      <div
        className="w-0 h-0 mx-auto"
        style={{
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: `6px solid ${isBottleneck ? '#eab308' : '#475569'}`,
        }}
      />
    </div>
  );
};

/**
 * Progress ring for showing utilization
 */
export const UtilizationRing = ({
  utilization = 0,
  size = 40,
  strokeWidth = 4,
  showLabel = true
}) => {
  const color = utilizationColor(utilization);
  const percentage = Math.round(utilization * 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (utilization * circumference);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#334155"
          strokeWidth={strokeWidth}
        />
        {/* Progress ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-300"
        />
      </svg>
      {showLabel && (
        <div
          className="absolute inset-0 flex items-center justify-center text-xs font-bold"
          style={{ color }}
        >
          {percentage}
        </div>
      )}
    </div>
  );
};

/**
 * Simple traffic flow indicator
 */
export const TrafficFlowIndicator = ({ qps, direction = 'right', animated = true }) => {
  const color = qps > 50000 ? '#22c55e' : qps > 10000 ? '#eab308' : '#94a3b8';

  return (
    <div className="flex items-center gap-1">
      {animated && (
        <div
          className="flex gap-0.5"
          style={{
            animation: direction === 'right' ? 'flowRight 1s infinite' : 'flowLeft 1s infinite'
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full"
              style={{
                backgroundColor: color,
                opacity: 1 - (i * 0.3)
              }}
            />
          ))}
        </div>
      )}
      <span className="text-xs" style={{ color }}>
        {formatQps(qps)}
      </span>
    </div>
  );
};

export default NodeUtilizationBadge;
